import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, mediaSrc, fmtTime, fmtDate, translateMessages } from "../api";
import { useApp } from "../context/AppContext";
import { PhotoTimeline } from "../components/PhotoTimeline";
import { IssueCard, TaskCard, ConfirmDeleteDialog } from "../components/Artifacts";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { ContactsDialog } from "./MessagesPage";
import { PdfViewer } from "../components/PdfViewer";
import { AddressLink } from "../components/AddressLink";
import { WeatherChip } from "../components/WeatherChip";
import { PdfThumb } from "../components/PdfThumb";
import { AskJob } from "../components/AskJob";
import { JobCustomerTalks } from "../components/JobCustomerTalks";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { useArtifactTranslations } from "../hooks/useArtifactTranslations";
import {
  ArrowLeft, CheckCircle2, FileText, MessageSquare, Mic, Image as ImageIcon,
  AlertTriangle, Ruler, Package, CheckSquare, Lock, Megaphone, HelpCircle, UsersRound, MessageSquarePlus, ChevronRight, User, Video, Sparkles, Trash2, Share2, Forward,
} from "lucide-react";
import { shareNative, mediaFilename } from "../lib/share";
import { ForwardDialog } from "../components/ForwardDialog";

const typeIcons = {
  text: MessageSquare, photo: ImageIcon, voice: Mic, issue: AlertTriangle,
  measurement: Ruler, material_request: Package, task: CheckSquare, question: HelpCircle, document: FileText, system: MessageSquare,
};

export default function JobHub() {
  const { jobId } = useParams();
  const { user, t, lang } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [job, setJob] = useState(null);
  const [team, setTeam] = useState([]);
  const [issues, setIssues] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [daySummaries, setDaySummaries] = useState([]); // past-job day recaps (AI)
  const [documents, setDocuments] = useState([]);
  const [comm, setComm] = useState(null);
  const [jobConvs, setJobConvs] = useState(null); // {groups, dms}
  const [contactsOpen, setContactsOpen] = useState(false);
  const [pdfDoc, setPdfDoc] = useState(null); // in-app PDF viewer (no new tab)
  const [forwardDoc, setForwardDoc] = useState(null);
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  const [issuesFilter, setIssuesFilter] = useState("open"); // all | open | resolved
  const [tasksFilter, setTasksFilter] = useState("open"); // all | open | resolved(=done)
  const [deleteDoc, setDeleteDoc] = useState(null); // ADMIN moderation: file pending delete confirmation
  const [deleteGroup, setDeleteGroup] = useState(null); // ADMIN moderation: job group pending delete confirmation
  const [deleteMsg, setDeleteMsg] = useState(null); // ADMIN moderation: communication message pending delete
  const [deleteDm, setDeleteDm] = useState(null); // ADMIN moderation: DM row — delete all job-linked messages
  const isAdmin = user?.role === "admin" || !!user?.super_admin;
  const commReqRef = useRef(new Set());

  const doDeleteDoc = async () => {
    if (!deleteDoc) return;
    try {
      await api.delete(`/media/${deleteDoc.id}`);
      toast.success(t("deleted"));
      setDeleteDoc(null);
      loadTabRef.current && loadTabRef.current("files");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const doDeleteGroup = async () => {
    if (!deleteGroup) return;
    try {
      await api.delete(`/conversations/${deleteGroup.id}`);
      toast.success(t("deleted"));
      setDeleteGroup(null);
      loadTabRef.current && loadTabRef.current("conversations");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const doDeleteMsg = async () => {
    if (!deleteMsg) return;
    try {
      await api.delete(`/messages/${deleteMsg.id}`);
      toast.success(t("deleted"));
      setDeleteMsg(null);
      loadTabRef.current && loadTabRef.current("communication");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const doDeleteDm = async () => {
    if (!deleteDm) return;
    try {
      await api.delete(`/jobs/${jobId}/dm-links/${deleteDm.id}`);
      toast.success(t("deleted"));
      setDeleteDm(null);
      loadTabRef.current && loadTabRef.current("conversations");
    } catch (e) {
      toast.error("Failed");
    }
  };
  const loadTabRef = useRef(null);

  // Issue/task titles in the viewer's language (lazy, cached)
  useArtifactTranslations(issues, setIssues, "issue", lang);
  useArtifactTranslations(tasks, setTasks, "task", lang);

  // Automatic translation of the communication feed into viewer's language
  useEffect(() => {
    if (!comm?.items?.length) return;
    const need = comm.items.filter((m) =>
      ((m.content && m.content.trim()) || (m.meta?.transcript && m.meta.transcript.trim())) &&
      !(m.translations && m.translations[lang]) &&
      !commReqRef.current.has(m.id + lang));
    if (need.length === 0) return;
    need.forEach((m) => commReqRef.current.add(m.id + lang));
    translateMessages(need.map((m) => m.id), lang)
      .then((map) => {
        if (!map || Object.keys(map).length === 0) return;
        setComm((prev) => (prev ? {
          ...prev,
          items: prev.items.map((m) => (map[m.id] ? { ...m, translations: { ...(m.translations || {}), [lang]: map[m.id] } } : m)),
        } : prev));
      })
      .catch(() => {});
  }, [comm, lang]);

  const loadJob = useCallback(async () => {
    const res = await api.get(`/jobs/${jobId}`);
    setJob(res.data);
    // Job team (crew leader + supervisor + office) for task assignment and overview
    const usersRes = await api.get("/users");
    const memberIds = new Set([...(res.data.installer_ids || []), res.data.supervisor_id].filter(Boolean));
    const teamUsers = usersRes.data.filter((u) => memberIds.has(u.id) || u.role === "office");
    setTeam(teamUsers);
  }, [jobId]);

  const loadTab = useCallback(async (which) => {
    if (which === "issues") setIssues((await api.get(`/issues?job_id=${jobId}`)).data);
    if (which === "tasks") setTasks((await api.get(`/tasks?job_id=${jobId}`)).data);
    if (which === "overview") {
      // Past jobs: day-by-day AI summaries to quickly recall what happened
      try { setDaySummaries((await api.get(`/jobs/${jobId}/daily-summaries`)).data); } catch (e) { setDaySummaries([]); }
    }
    if (which === "files") setDocuments((await api.get(`/jobs/${jobId}/documents`)).data);
    if (which === "communication") setComm((await api.get(`/jobs/${jobId}/communication?user_id=${user.id}`)).data);
    if (which === "conversations") setJobConvs((await api.get(`/jobs/${jobId}/conversations?user_id=${user.id}`)).data);
  }, [jobId, user.id]);
  loadTabRef.current = loadTab;
  useEffect(() => { loadJob(); }, [loadJob]);
  useEffect(() => { if (tab !== "photos") loadTab(tab); }, [tab, loadTab]);
  // Buddy: "abre o PDF" → /jobs/:id?tab=files&open=pdf → show the work-order PDF full-screen right away
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current || searchParams.get("open") !== "pdf" || tab !== "files" || !documents.length) return;
    const pdf = documents.find((d) => d.pmhr_pdf) || documents.find((d) => /pdf/i.test(d.mime_type || d.content_type || d.filename || ""));
    if (pdf) { autoOpenedRef.current = true; setPdfDoc(pdf); }
  }, [documents, tab, searchParams]);

  const completeJob = async () => {
    await api.post(`/jobs/${jobId}/complete`, { user_id: user.id });
    toast.success(t("job_completed"));
    loadJob();
  };

  if (!job) {
    return <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">...</div>;
  }

  const statusColor = { active: "bg-primary text-primary-foreground", scheduled: "bg-accent text-accent-foreground", completed: "bg-[hsl(var(--success))] text-white" };
  // Pseudo-conversation for photo tools (task assignment); id null = no chat posting
  const photoConversation = { id: null, job_id: jobId, participants: team };

  // group communication items by date
  const commGroups = [];
  if (comm?.items) {
    let currentDate = null;
    comm.items.forEach((m) => {
      const d = fmtDate(m.created_at, lang);
      if (d !== currentDate) {
        currentDate = d;
        commGroups.push({ date: d, items: [] });
      }
      commGroups[commGroups.length - 1].items.push(m);
    });
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Job header */}
      <div className="border-b bg-background px-4 py-3">
        <div className="flex items-center gap-3 max-w-6xl mx-auto w-full">
          <Button variant="ghost" size="icon" data-testid="job-hub-back" onClick={() => navigate(-1)} className="h-9 w-9 shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono-plex text-sm font-bold text-primary">JOB #{job.job_number}</span>
              <span className="font-display font-semibold truncate">{job.customer_name}</span>
              {!job.removed_from_source && <Badge className={`${statusColor[job.status]} text-[10px]`}>{t(job.status)}</Badge>}
              {job.removed_from_source && (
                <Badge
                  data-testid="job-hub-removed-badge"
                  variant="outline"
                  className="text-[10px] font-semibold uppercase tracking-wide border-destructive/40 text-destructive bg-destructive/10"
                >
                  {t("removed_from_matrix")}
                </Badge>
              )}
              {job.job_type && (
                <Badge
                  data-testid="job-hub-type-badge"
                  variant={job.job_type === "service" ? "default" : "outline"}
                  className={`text-[10px] font-semibold uppercase tracking-wide ${job.job_type === "service" ? "bg-accent text-accent-foreground hover:bg-accent" : "border-primary/40 text-primary"}`}
                >
                  {job.job_type === "service" ? t("job_type_service") : t("job_type_install")}
                </Badge>
              )}
              {job.total_days > 1 && (
                <Badge data-testid="job-hub-day-badge" variant="secondary" className="text-[10px] font-semibold">
                  {job.day_number
                    ? t("day_of_days").replace("{d}", job.day_number).replace("{n}", job.total_days)
                    : t("days_count").replace("{n}", job.total_days)}
                </Badge>
              )}
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5 min-w-0">
              <AddressLink address={job.address} city={job.city} />
              <span className="truncate shrink-0">· {t("crew")} {job.crew?.code} · {job.supervisor?.name}</span>
            </div>
          </div>
          <WeatherChip city={job.city} size="sm" />
          {user.role === "installer" && job.status === "active" && (
            <Button size="sm" data-testid="complete-job-button" onClick={completeJob} className="shrink-0 bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90 text-white">
              <CheckCircle2 className="h-4 w-4 mr-1.5" />
              <span className="hidden sm:inline">{t("complete_job")}</span>
              <span className="sm:hidden">OK</span>
            </Button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
        <div className="border-b bg-background overflow-x-auto">
          <TabsList data-testid="job-hub-tabs" className="w-max min-w-full justify-start rounded-none bg-transparent h-11 px-4">
            <TabsTrigger value="overview" data-testid="job-hub-tab-overview">{t("overview")}</TabsTrigger>
            <TabsTrigger value="conversations" data-testid="job-hub-tab-conversations">{t("conversations")}</TabsTrigger>
            <TabsTrigger value="customer" data-testid="job-hub-tab-customer">{t("job_tab_customer")}</TabsTrigger>
            <TabsTrigger value="communication" data-testid="job-hub-tab-communication">{t("communication")}</TabsTrigger>
            <TabsTrigger value="photos" data-testid="job-hub-tab-photos">{t("media_tab")} <span className="ml-1 text-[10px] text-muted-foreground">{job.photo_count}</span></TabsTrigger>
            <TabsTrigger value="issues" data-testid="job-hub-tab-issues">{t("issues")} {job.open_issues > 0 && <Badge className="ml-1.5 bg-destructive text-white text-[9px] px-1 py-0">{job.open_issues}</Badge>}</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="job-hub-tab-tasks">{t("tasks")} {job.open_tasks > 0 && <Badge className="ml-1.5 bg-accent text-accent-foreground text-[9px] px-1 py-0">{job.open_tasks}</Badge>}</TabsTrigger>
            <TabsTrigger value="files" data-testid="job-hub-tab-files">{t("files")}</TabsTrigger>
            <TabsTrigger value="ask" data-testid="job-hub-tab-ask" className="text-primary data-[state=active]:text-primary">
              <Sparkles className="h-3.5 w-3.5 mr-1" /> {t("ask_job_tab")}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="flex-1 overflow-y-auto mt-0 data-[state=inactive]:hidden">
          <div className="max-w-3xl mx-auto w-full p-4 space-y-5">
            <Card className="p-5 space-y-4" data-testid="job-info-card">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InfoRow label={t("customer")} value={job.customer_name} />
                <InfoRow label={t("address")} value={<AddressLink address={job.address} city={job.city} iconClass="h-3.5 w-3.5" className="text-sm font-medium" />} />
                <InfoRow label={t("schedule")} value={`${new Date(job.scheduled_date).toLocaleDateString(lang === "pt" ? "pt-BR" : "en-US", { weekday: "long", month: "short", day: "numeric" })} · ${job.scheduled_time}`} />
                <InfoRow label={t("units")} value={job.description} />
                <InfoRow label={t("crew")} value={`${job.crew?.code} — ${job.crew?.name || ""}`} />
                <InfoRow label={t("supervisor_label")} value={job.supervisor?.name} />
              </div>
              {job.notes && (
                <div className="pt-3 border-t">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{t("job_notes")}</div>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="job-notes">{job.notes}</p>
                </div>
              )}
            </Card>

            {/* Day summaries — PAST jobs only: quick recall of what happened */}
            {job.time_bucket === "past" && daySummaries.length > 0 && (
              <div>
                <h3 className="font-display font-semibold text-base mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-accent-foreground" /> {t("job_day_summaries")}
                  <Badge variant="secondary" className="text-[10px]">{daySummaries.length}</Badge>
                </h3>
                <div className="space-y-2" data-testid="job-day-summaries-list">
                  {daySummaries.map((s) => {
                    const text = lang === "pt" ? s.summary_pt : s.summary_en;
                    const highlights = (lang === "pt" ? s.highlights_pt : s.highlights_en) || [];
                    return (
                      <Card key={s.id} data-testid={`job-day-summary-${s.date}`} className="p-3.5 border-l-4 border-l-primary/50">
                        <div className="text-xs text-muted-foreground font-medium mb-1 tabular-nums">
                          {new Date(`${s.date}T12:00:00`).toLocaleDateString(lang === "pt" ? "pt-BR" : "en-US", { weekday: "long", day: "numeric", month: "short", year: "numeric" })}
                        </div>
                        <p className="text-sm leading-relaxed">{text}</p>
                        {highlights.length > 0 && (
                          <ul className="mt-1.5 space-y-0.5">
                            {highlights.map((h, i) => (
                              <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                                <span className="h-1 w-1 rounded-full bg-primary mt-1.5 shrink-0" />
                                <span>{h}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* CONVERSATIONS — DM views + job groups (unified model) */}
        <TabsContent value="conversations" className="flex-1 overflow-y-auto mt-0 data-[state=inactive]:hidden">
          <div className="max-w-3xl mx-auto w-full p-4" data-testid="job-conversations-panel">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-muted-foreground pr-3">{t("talk_about_job")}</p>
              <Button size="sm" data-testid="job-new-conversation-button" onClick={() => setContactsOpen(true)}>
                <MessageSquarePlus className="h-4 w-4 mr-1.5" /> {t("new_conversation")}
              </Button>
            </div>

            {/* Job groups */}
            {jobConvs?.groups?.length > 0 && (
              <div className="mb-5">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-1 pb-1.5">{t("job_groups")}</div>
                <div className="space-y-1" data-testid="job-groups-list">
                  {jobConvs.groups.map((g) => (
                    <Card
                      key={g.id}
                      data-testid={`job-group-row-${g.id}`}
                      onClick={() => navigate(`/chat/${g.id}`)}
                      className="p-3 cursor-pointer hover:bg-muted/40 transition-colors flex items-center gap-3"
                    >
                      <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <UsersRound className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{g.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {g.member_count} {t("members")}
                          {g.last_message ? ` · ${g.last_message.sender_name}: ${(g.last_message.content || "").slice(0, 40)}` : ""}
                        </div>
                      </div>
                      {g.unread > 0 && (
                        <Badge className="bg-primary text-primary-foreground text-[10px]">{g.unread}</Badge>
                      )}
                      {isAdmin && (
                        <button
                          type="button"
                          data-testid={`job-group-delete-btn-${g.id}`}
                          title={t("delete")}
                          onClick={(e) => { e.stopPropagation(); setDeleteGroup(g); }}
                          className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* DM views — full conversations filtered by this job */}
            {jobConvs?.dms?.length > 0 && (
              <div className="mb-5">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-1 pb-1.5">{t("direct")}</div>
                <div className="space-y-1" data-testid="job-dms-list">
                  {jobConvs.dms.map((d) => (
                    <Card
                      key={d.id}
                      data-testid={`job-dm-row-${d.id}`}
                      onClick={() => { if (d.is_participant !== false) navigate(`/chat/${d.id}?job=${jobId}`); }}
                      className={`p-3 transition-colors flex items-center gap-3 ${d.is_participant !== false ? "cursor-pointer hover:bg-muted/40" : ""}`}
                    >
                      <Avatar className="h-10 w-10 shrink-0">
                        <AvatarFallback style={{ backgroundColor: d.other_user?.avatar_color || "#64748b", color: "#fff" }} className="text-xs font-semibold">
                          {(d.name || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{d.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {d.linked_count} {t("linked_msgs")}
                        </div>
                      </div>
                      <Badge variant="outline" className="text-[9px] px-1.5 font-mono-plex text-primary border-primary/40 shrink-0">
                        #{job.job_number}
                      </Badge>
                      {isAdmin && (
                        <button
                          type="button"
                          data-testid={`job-dm-delete-btn-${d.id}`}
                          title={t("delete")}
                          onClick={(e) => { e.stopPropagation(); setDeleteDm(d); }}
                          className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      {d.is_participant !== false && <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {jobConvs && jobConvs.groups.length === 0 && jobConvs.dms.length === 0 && (
              <div className="text-center py-14">
                <UsersRound className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-4">{t("no_conversations")}</p>
                <Button data-testid="job-empty-new-conversation" onClick={() => setContactsOpen(true)}>
                  <MessageSquarePlus className="h-4 w-4 mr-1.5" /> {t("new_conversation")}
                </Button>
              </div>
            )}
            {!jobConvs && (
              <div className="space-y-2">
                {[1, 2].map((i) => <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />)}
              </div>
            )}
          </div>
        </TabsContent>

        {/* COMMUNICATION — aggregated feed from all conversations */}
        <TabsContent value="customer" className="flex-1 overflow-y-auto mt-0 data-[state=inactive]:hidden">
          {tab === "customer" && <JobCustomerTalks jobId={jobId} />}
        </TabsContent>

        <TabsContent value="communication" className="flex-1 overflow-y-auto mt-0 data-[state=inactive]:hidden">
          <div className="max-w-3xl mx-auto w-full p-4" data-testid="job-communication-feed">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-muted-foreground pr-3">{t("communication")} — {comm?.items?.length || 0} {t("msgs")}</p>
            </div>
            {commGroups.length === 0 && <div className="text-center text-sm text-muted-foreground py-12">{t("no_messages")}</div>}
            {commGroups.map((g) => (
              <div key={g.date} className="mb-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-px bg-border flex-1" />
                  <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">{g.date}</span>
                  <div className="h-px bg-border flex-1" />
                </div>
                <div className="space-y-2">
                  {g.items.map((m) => {
                    const Icon = typeIcons[m.type] || MessageSquare;
                    const visShared = m.meta?.link_visibility === "shared";
                    const trc = m.translations?.[lang];
                    const dispContent = trc?.content?.trim() && trc.content.trim() !== (m.content || "").trim() ? trc.content : m.content;
                    const dispTranscript = trc?.transcript?.trim() && trc.transcript.trim() !== ((m.meta?.transcript || "").trim()) ? trc.transcript : m.meta?.transcript;
                    return (
                      <Card
                        key={m.id}
                        data-testid="communication-item"
                        onClick={() => navigate(`/chat/${m.conversation_id}`)}
                        className="p-3 cursor-pointer hover:bg-muted/40 transition-colors"
                      >
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Icon className="h-3.5 w-3.5" />
                          <span className="font-medium text-foreground">{m.route_label}</span>
                          {m.conversation_type === "dm" && (
                            visShared
                              ? <Megaphone className="h-3 w-3 text-[hsl(var(--success))]" title={t("shared")} />
                              : <Lock className="h-3 w-3" title={t("private")} />
                          )}
                          <span className="ml-auto shrink-0">{fmtTime(m.created_at)}</span>
                          {isAdmin && (
                            <button
                              type="button"
                              data-testid={`comm-msg-delete-btn-${m.id}`}
                              title={t("delete")}
                              onClick={(e) => { e.stopPropagation(); setDeleteMsg(m); }}
                              className="h-7 w-7 -my-1 rounded-full flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        <p className="text-sm mt-1 line-clamp-2">
                          {m.type === "voice" ? (dispTranscript || t("voice_message")) : m.type === "measurement" ? `${m.meta?.location || ""}: ${m.meta?.value || ""}` : dispContent}
                        </p>
                        {m.media?.filter((md) => md.kind === "image").length > 0 && (
                          <div className="flex gap-1.5 mt-2">
                            {m.media.filter((md) => md.kind === "image").slice(0, 4).map((md) => (
                              <img key={md.id} src={mediaSrc(md)} alt="" className="h-16 w-16 object-cover rounded-lg border" />
                            ))}
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="photos" className="flex-1 overflow-y-auto mt-0 data-[state=inactive]:hidden">
          <div className="max-w-6xl mx-auto w-full p-4">
            <PhotoTimeline jobId={jobId} conversation={photoConversation} />
          </div>
        </TabsContent>

        <TabsContent value="issues" className="flex-1 overflow-y-auto mt-0 data-[state=inactive]:hidden">
          <div className="max-w-3xl mx-auto w-full p-4 space-y-3" data-testid="job-issues-list">
            <Tabs value={issuesFilter} onValueChange={setIssuesFilter}>
              <TabsList data-testid="job-issues-filter">
                <TabsTrigger value="all" data-testid="job-issues-filter-all">{t("all")}</TabsTrigger>
                <TabsTrigger value="open" data-testid="job-issues-filter-open">{t("status_open")}</TabsTrigger>
                <TabsTrigger value="resolved" data-testid="job-issues-filter-resolved">{t("status_resolved")}</TabsTrigger>
              </TabsList>
            </Tabs>
            {(() => {
              const list = issuesFilter === "all" ? issues
                : issuesFilter === "resolved" ? issues.filter((i) => i.status === "resolved")
                : issues.filter((i) => i.status !== "resolved");
              return (
                <>
                  {list.length === 0 && <div className="text-center text-sm text-muted-foreground py-12">{t("no_issues")}</div>}
                  {list.map((issue) => (
                    <IssueCard key={issue.id} issue={issue} onChanged={() => { loadTab("issues"); loadJob(); }} />
                  ))}
                </>
              );
            })()}
          </div>
        </TabsContent>

        <TabsContent value="tasks" className="flex-1 overflow-y-auto mt-0 data-[state=inactive]:hidden">
          <div className="max-w-3xl mx-auto w-full p-4 space-y-3" data-testid="job-tasks-list">
            <Tabs value={tasksFilter} onValueChange={setTasksFilter}>
              <TabsList data-testid="job-tasks-filter">
                <TabsTrigger value="all" data-testid="job-tasks-filter-all">{t("all")}</TabsTrigger>
                <TabsTrigger value="open" data-testid="job-tasks-filter-open">{t("status_open")}</TabsTrigger>
                <TabsTrigger value="resolved" data-testid="job-tasks-filter-resolved">{t("status_resolved")}</TabsTrigger>
              </TabsList>
            </Tabs>
            {(() => {
              const list = tasksFilter === "all" ? tasks
                : tasksFilter === "resolved" ? tasks.filter((tk) => tk.status === "done")
                : tasks.filter((tk) => tk.status === "open");
              return (
                <>
                  {list.length === 0 && <div className="text-center text-sm text-muted-foreground py-12">{t("no_tasks")}</div>}
                  {list.map((task) => (
                    <TaskCard key={task.id} task={task} onChanged={() => { loadTab("tasks"); loadJob(); }} />
                  ))}
                </>
              );
            })()}
          </div>
        </TabsContent>

        <TabsContent value="files" className="flex-1 overflow-y-auto mt-0 data-[state=inactive]:hidden">
          <div className="max-w-3xl mx-auto w-full p-4 space-y-3" data-testid="job-documents-list">
            {documents.length === 0 && <div className="text-center text-sm text-muted-foreground py-12">{t("no_documents")}</div>}
            {documents.map((d) => (
              <Card
                key={d.id}
                className={`p-4 flex items-center gap-3 ${d.kind !== "video" ? "cursor-pointer hover:bg-muted/40 transition-colors" : ""}`}
                data-testid="job-file-row"
                onClick={() => { if (d.kind !== "video") setPdfDoc(d); }}
              >
                {d.kind === "video" ? (
                  <Video className="h-5 w-5 text-primary shrink-0" />
                ) : (
                  <PdfThumb media={d} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{d.filename || (d.kind === "video" ? "Video" : "Document")}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(d.created_at, lang)}</div>
                </div>
                <button
                  type="button"
                  data-testid="job-file-forward-btn"
                  aria-label={t("send_to_chat")}
                  className="h-8 w-8 rounded-md hover:bg-muted flex items-center justify-center text-primary shrink-0"
                  onClick={(e) => { e.stopPropagation(); setForwardDoc(d); }}
                >
                  <Forward className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  data-testid="job-file-share-btn"
                  aria-label={t("share")}
                  className="h-8 w-8 rounded-md hover:bg-muted flex items-center justify-center text-primary shrink-0"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const ok = await shareNative({ url: mediaSrc(d), filename: d.kind === "video" ? mediaFilename(d, job) : d.filename, title: d.filename });
                    if (!ok) toast.info(t("share_unavailable"));
                  }}
                >
                  <Share2 className="h-4 w-4" />
                </button>
                {d.kind === "video" ? (
                  <a href={mediaSrc(d)} target="_blank" rel="noreferrer" className="text-xs text-primary underline" onClick={(e) => e.stopPropagation()}>Open</a>
                ) : (
                  <button
                    type="button"
                    data-testid="job-file-open-btn"
                    className="text-xs text-primary underline"
                    onClick={(e) => { e.stopPropagation(); setPdfDoc(d); }}
                  >
                    Open
                  </button>
                )}
                {isAdmin && (
                  <button
                    type="button"
                    data-testid={`job-file-delete-btn-${d.id}`}
                    title={t("delete")}
                    onClick={(e) => { e.stopPropagation(); setDeleteDoc(d); }}
                    className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* ASK THE JOB — AI Q&A with tappable citations */}
        <TabsContent value="ask" className="flex-1 flex flex-col min-h-0 overflow-hidden mt-0 data-[state=inactive]:hidden">
          <div className="max-w-3xl mx-auto w-full flex-1 flex flex-col min-h-0 p-4 pt-3">
            <AskJob jobId={jobId} />
          </div>
        </TabsContent>
      </Tabs>

      <PdfViewer open={!!pdfDoc} media={pdfDoc} onClose={() => setPdfDoc(null)} />
      <ForwardDialog open={!!forwardDoc} media={forwardDoc ? [forwardDoc] : []} onClose={() => setForwardDoc(null)} />

      {/* ADMIN moderation: confirm dialogs for deleting a job group or a job file */}
      <ConfirmDeleteDialog
        open={!!deleteGroup}
        onClose={() => setDeleteGroup(null)}
        onConfirm={doDeleteGroup}
        title={`${deleteGroup?.name || ""} — ${t("delete_group_conv_confirm")}`}
        t={t}
        testid="job-group"
      />
      <ConfirmDeleteDialog
        open={!!deleteDoc}
        onClose={() => setDeleteDoc(null)}
        onConfirm={doDeleteDoc}
        title={`${deleteDoc?.filename || ""} — ${t("delete_file_confirm")}`}
        t={t}
        testid="job-file"
      />
      <ConfirmDeleteDialog
        open={!!deleteMsg}
        onClose={() => setDeleteMsg(null)}
        onConfirm={doDeleteMsg}
        title={`"${((deleteMsg?.content || deleteMsg?.meta?.transcript || "") + "").slice(0, 80)}" — ${t("delete_comm_msg_confirm")}`}
        t={t}
        testid="comm-msg"
      />
      <ConfirmDeleteDialog
        open={!!deleteDm}
        onClose={() => setDeleteDm(null)}
        onConfirm={doDeleteDm}
        title={`${deleteDm?.name || ""} — ${t("delete_dm_links_confirm").replace("{n}", deleteDm?.linked_count ?? "")}`}
        t={t}
        testid="job-dm"
      />

      {/* New conversation about this job (DM in job context or job group) */}
      <ContactsDialog
        open={contactsOpen}
        onClose={() => { setContactsOpen(false); if (tab === "conversations") loadTab("conversations"); }}
        jobId={jobId}
        jobNumber={job.job_number}
      />
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-medium mt-0.5 min-w-0 break-words">{value}</div>
    </div>
  );
}
